// Returns a box size in % that matches the media's natural ratio, capped at maxPct.
// widthPct/heightPct = mediaRatio / canvasRatio because the box width is % of canvas
// width and height is % of canvas height. Falls back to maxPct × maxPct on failure.
export default function getMediaBoxSize(url, type, opts = {}) {
  const maxPct = opts.maxPct ?? 44;
  const timeoutMs = opts.timeoutMs ?? 8000;
  const cRatio =
    opts.canvasRatio || (typeof window !== "undefined" && window.canvasRatio) || 16 / 9;
  const fallback = { width: maxPct, height: maxPct, widthUnit: "%", heightUnit: "%" };

  return new Promise((resolve) => {
    if (!url || typeof document === "undefined") {
      resolve(fallback);
      return;
    }

    let done = false;
    let timer = null;
    let el = null;

    const cleanup = () => {
      if (timer) clearTimeout(timer);
      if (el) {
        try {
          el.onload = null;
          el.onerror = null;
          el.src = "";
        } catch {
          /* ignore */
        }
        el = null;
      }
    };

    const finish = (result) => {
      if (done) return;
      done = true;
      cleanup();
      resolve(result);
    };

    const computeAndFinish = (mW, mH) => {
      if (!mW || !mH) {
        finish(fallback);
        return;
      }
      const ratio = mW / mH / cRatio; // = widthPct / heightPct
      let width;
      let height;
      if (ratio >= 1) {
        width = maxPct;
        height = Math.max(1, Math.round(maxPct / ratio));
      } else {
        height = maxPct;
        width = Math.max(1, Math.round(maxPct * ratio));
      }
      finish({ width, height, widthUnit: "%", heightUnit: "%" });
    };

    try {
      if (type === "video") {
        el = document.createElement("video");
        el.preload = "metadata";
        el.muted = true;
        el.addEventListener(
          "loadedmetadata",
          () => computeAndFinish(el.videoWidth, el.videoHeight),
          { once: true }
        );
        el.addEventListener("error", () => finish(fallback), { once: true });
        el.src = url;
      } else {
        el = new Image();
        el.onload = () => computeAndFinish(el.naturalWidth, el.naturalHeight);
        el.onerror = () => finish(fallback);
        el.src = url;
      }
      timer = setTimeout(() => finish(fallback), timeoutMs);
    } catch {
      finish(fallback);
    }
  });
}
